import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import Button from "./ui/Button"
import Field from "./ui/Field"
import Panel from "./ui/Panel"
import Stat from "./ui/Stat"

function monthlyPayment(principal, annualRate, months) {
  if (months <= 0 || principal <= 0) return 0
  const r = annualRate / 100 / 12
  if (r === 0) return principal / months
  return (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1)
}

function formatPln(n) {
  return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(n)
}

function ToggleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-2.5 py-2 text-xs font-bold transition-colors sm:px-4 sm:text-sm ${
        active ? "bg-primary text-white" : "border border-border hover:border-primary/40"
      }`}
    >
      {children}
    </button>
  )
}

function readUrlNumber(key, fallback) {
  if (typeof window === "undefined") return fallback
  const raw = new URLSearchParams(window.location.search).get(key)
  if (!raw) return fallback
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

export default function MortgageCalculator({ defaultRate = 6.75, wibor6m = null, marginPp = null, sourcePage = "/" }) {
  const { t } = useTranslation()
  const [price, setPrice] = useState(() => readUrlNumber("price", 800000))
  const [downPct, setDownPct] = useState(20)
  const [term, setTerm] = useState(25)
  const [rate, setRate] = useState(Number(defaultRate))
  const [isNew, setIsNew] = useState(true)
  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState("")

  const downPayment = price * (downPct / 100)
  const principal = price - downPayment
  const months = term * 12
  const payment = monthlyPayment(principal, rate, months)
  const totalPaid = payment * months
  const totalInterest = totalPaid - principal
  const ltv = ((principal / price) * 100).toFixed(1)
  const terms = [10, 15, 20, 25, 30]

  async function submitLead(e) {
    e.preventDefault()
    setError("")
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch("/api/v1/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": token },
        body: JSON.stringify({
          lead: {
            email,
            name,
            lead_type: "mortgage",
            message: `Price: ${price} PLN, Down: ${downPct}%, Term: ${term}y, Rate: ${rate}%`,
            source_page: sourcePage,
          },
        }),
      })
      if (res.ok) setSubmitted(true)
      else setError(t("common.errors.submitFailed"))
    } catch {
      setError(t("common.errors.submitFailedLater"))
    }
  }

  return (
    <Panel>
      <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
        <div className="space-y-6">
          <fieldset>
            <legend className="text-sm font-bold">{t("mortgage.propertyPrice")}</legend>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <input
                type="range"
                min={100000}
                max={3000000}
                step={10000}
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full flex-1 accent-primary"
                aria-label={t("mortgage.propertyPrice")}
              />
              <Field
                type="number"
                value={price}
                onChange={(e) => setPrice(Number(e.target.value))}
                className="w-full font-mono sm:w-32"
                aria-label={t("mortgage.propertyPrice")}
              />
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-bold">{t("mortgage.downPayment", { pct: downPct })}</legend>
            <input
              type="range"
              min={10}
              max={50}
              value={downPct}
              onChange={(e) => setDownPct(Number(e.target.value))}
              className="mt-2 w-full accent-primary"
              aria-label={t("mortgage.downPayment", { pct: downPct })}
            />
            <p className="mt-1 text-sm text-muted font-mono">{formatPln(downPayment)}</p>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-bold">{t("mortgage.loanTerm")}</legend>
            <div className="mt-2 flex flex-wrap gap-2">
              {terms.map((years) => (
                <ToggleButton key={years} active={term === years} onClick={() => setTerm(years)}>
                  {t("common.years", { count: years })}
                </ToggleButton>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-sm font-bold">{t("mortgage.interestRate")}</legend>
            <Field
              type="number"
              step={0.01}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              className="mt-2 w-32 font-mono"
              aria-label={t("mortgage.interestRate")}
            />
            {wibor6m != null && marginPp != null && (
              <p className="mt-2 text-xs text-muted">
                {t("mortgage.wiborDefault", { wibor: wibor6m, margin: marginPp })}
              </p>
            )}
          </fieldset>

          <fieldset>
            <legend className="text-sm font-bold">{t("mortgage.propertyType")}</legend>
            <div className="mt-2 flex gap-2">
              <ToggleButton active={isNew} onClick={() => setIsNew(true)}>{t("mortgage.newBuild")}</ToggleButton>
              <ToggleButton active={!isNew} onClick={() => setIsNew(false)}>{t("mortgage.existing")}</ToggleButton>
            </div>
          </fieldset>
        </div>

        <div>
          <Stat label={t("mortgage.monthlyPayment")} value={formatPln(payment)} valueClassName="text-4xl">
            <dl className="mt-5 grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <dt className="ui-stat__label">{t("mortgage.totalInterest")}</dt>
                <dd className="font-mono text-lg font-semibold mt-1">{formatPln(totalInterest)}</dd>
              </div>
              <div>
                <dt className="ui-stat__label">{t("mortgage.totalCost")}</dt>
                <dd className="font-mono text-lg font-semibold mt-1">{formatPln(totalPaid + downPayment)}</dd>
              </div>
              <div>
                <dt className="ui-stat__label">{t("mortgage.ltv")}</dt>
                <dd className="font-mono text-lg font-semibold mt-1">{ltv}%</dd>
              </div>
              <div>
                <dt className="ui-stat__label">{t("mortgage.loanAmount")}</dt>
                <dd className="font-mono text-lg font-semibold mt-1">{formatPln(principal)}</dd>
              </div>
            </dl>
          </Stat>

          <p className="mt-4 text-sm">
            <a href="/guides/mortgage-for-foreigners-poland" className="font-bold text-primary hover:underline">
              {t("mortgage.foreignerGuide")}
            </a>
          </p>

          {!submitted ? (
            <form onSubmit={submitLead} className="mt-6 space-y-3">
              <p className="text-sm font-bold">{t("mortgage.brokerHeading")}</p>
              <Field
                type="text"
                placeholder={t("mortgage.namePlaceholder")}
                aria-label={t("mortgage.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Field
                type="email"
                required
                placeholder={t("mortgage.emailPlaceholder")}
                aria-label={t("mortgage.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" full>
                {t("mortgage.requestBroker")}
              </Button>
              {error && <p className="text-sm text-negative">{error}</p>}
            </form>
          ) : (
            <p className="mt-6 text-sm text-positive font-bold">{t("mortgage.thanks")}</p>
          )}
        </div>
      </div>
    </Panel>
  )
}
